import { useEffect, useRef, useState } from 'react';
import {
  acumular,
  laMasNitida,
  medirDetalle,
  mirar,
  pistaDeEncuadre,
  FOTOS_DE_LA_RAFAGA,
  MIRADAS_BUENAS_PARA_DISPARAR,
  MS_ENTRE_FOTOS_DE_LA_RAFAGA,
  MS_ENTRE_MIRADAS,
  RACHA_INICIAL,
  type Objetivo,
} from '@validate/core';
import { fotogramaEnGrises } from './camara.js';

/**
 * La captura automática: el lazo que mira, decide y dispara. Sin botón.
 *
 * Es el "Detección automática..." del Figma. Aquí NO se decide nada: `mirar()` y `acumular()`
 * viven en `packages/core/src/verificacion/encuadre.ts` y están probados sin cámara. Este
 * fichero solo saca píxeles del vídeo, se los pasa al core, y hace lo que el core dijo.
 *
 * (La excepción, provisional y declarada, son las exigencias del documento — ver abajo.)
 *
 * ── EL LAZO ES DE UN DISPARO, Y LA CLAVE ES LO QUE LO REARMA ───────────────────
 *
 * En cuanto dispara, el lazo se para. No vuelve hasta que cambien `objetivo`, `activo` o
 * `clave`. La pantalla del documento pasa la cara como clave: sin esto, el reverso no
 * capturaba jamás (primera prueba real, 18/07/2026).
 *
 * ── CARGANDO BLOQUEA. SIEMPRE. ─────────────────────────────────────────────────
 *
 * La cuarta prueba real encontró el agujero más fino: mientras el detector carga (1-3 s),
 * la versión anterior degradaba la exigencia a "permitir" — y la racha tarda ~1,1 s, así
 * que GANA la carrera contra la carga y captura lo que haya delante (un teclado, esa vez).
 * Ahora "cargando" bloquea el disparo con su propia pista; solo el fallo DEFINITIVO del
 * motor degrada a capturar por calidad, como v1. La diferencia entre "todavía no sé" y
 * "nunca voy a saber" es exactamente la diferencia entre esperar dos segundos y quedarse
 * ciego para siempre — y se tratan distinto.
 *
 * ── EL REVERSO SE VALIDA EN VIVO: EL MRZ SE VE A SIMPLE PÍXEL ──────────────────
 *
 * El reverso no tiene cara impresa que exigir, pero tiene algo mejor: el MRZ — tres líneas
 * densas de caracteres monoespaciados en la parte baja del carnet. Eso deja una firma en los
 * píxeles que no necesita ningún modelo: filas con ~30% de tinta y decenas de transiciones
 * claro↔oscuro. Una mesa no la tiene; un teclado tampoco (sus teclas son mayoritariamente
 * oscuras: el ratio de tinta se pasa). La exigencia corre en vivo, ANTES de disparar, que es
 * donde tiene que estar — el `/reverso` del servidor queda como red de seguridad.
 *
 * Con válvula: si todo lo demás está bien durante 12 s y solo falta el MRZ, se deja pasar.
 * Un reflejo sobre laminado puede comerse la banda, y atrapar a una persona real en un bucle
 * por nuestra heurística sería pagar un falso negativo con un abandono.
 *
 * ── LA FOTO IMPRESA SE BUSCA EN UN RECORTE DEL MARCO ───────────────────────────
 *
 * BlazeFace corto alcance reduce su entrada a ~128px: la foto impresa a cuadro completo mide
 * ~15px ahí y es invisible (segunda prueba real). En el recorte del marco ocupa ~25% y sí se
 * ve. El recorte usa el rect REAL del marco en pantalla, mapeado a vídeo con la aritmética
 * de object-cover (tercera prueba real: eran geometrías distintas y había que "alejar" el
 * DNI).
 *
 * DEUDA: estas exigencias pertenecen a `encuadre.ts` del core, con tests. Si tocas la
 * regla, múdala.
 */

/** Lo mismo que usa v1: BlazeFace corto alcance. */
const MODELO_ROSTRO = '/models/blaze_face_short_range.tflite';
const WASM = '/mediapipe/wasm';

/**
 * Confianza mínima para decir que hay una cara. 0.2 es de v1 y es BAJA a propósito: aquí un
 * falso positivo no aprueba a nadie; un falso negativo deja a una persona real mirando una
 * pantalla que no reacciona.
 */
const CONFIANZA_MINIMA = 0.2;

/** Si la pantalla no entrega el marco: el centro, 86% del ancho, proporción ID-1. */
const ANCHO_DEL_MARCO = 0.86;
const PROPORCION_ID1 = 1.586;

/** A qué tamaño se lleva el recorte antes de analizar. */
const ANCHO_DEL_RECORTE = 320;

/** Margen alrededor del marco: la mano no es un trípode. Fracción del ancho del marco. */
const MARGEN_DE_DETECCION = 0.06;
const MARGEN_DE_CAPTURA = 0.08;

/**
 * Dentro del recorte del marco, la foto del DNI ocupa entre un octavo y dos tercios del
 * ancho. Menos es ruido; más es una cara VIVA asomada al marco — y eso no es un documento.
 */
const ROSTRO_IMPRESO_MINIMO = 0.08;
const ROSTRO_IMPRESO_MAXIMO = 0.65;

/**
 * La firma del MRZ en los píxeles, medida sobre el recorte de 320 de ancho:
 * filas con tinta entre el 5% y el 50%, y al menos este número de transiciones
 * claro↔oscuro (30 caracteres monoespaciados dejan ~60; se pide mucho menos).
 */
const TRANSICIONES_MINIMAS_POR_FILA = 16;
const TINTA_MINIMA = 0.05;
const TINTA_MAXIMA = 0.5;
/** Cuántas filas "de texto" hacen una banda MRZ. Tres líneas dejan ~20; se piden 5. */
const FILAS_DE_TEXTO_MINIMAS = 5;
/** La válvula: si solo falta el MRZ durante este tiempo, se deja pasar. */
const MS_DE_PACIENCIA_CON_EL_MRZ = 12_000;

interface Detector {
  detectForVideo(video: HTMLVideoElement, ms: number): { detections: Deteccion[] };
}

interface DetectorDeFotos {
  detect(imagen: HTMLCanvasElement): { detections: Deteccion[] };
}

interface Deteccion {
  boundingBox?: { width: number } | undefined;
  categories?: { score: number }[] | undefined;
}

/** Carga MediaPipe una sola vez para toda la aplicación. Import dinámico: son megas. */
let promesaDelDetector: Promise<Detector | null> | null = null;

async function cargarDetector(): Promise<Detector | null> {
  promesaDelDetector ??= (async () => {
    try {
      const vision = await import('@mediapipe/tasks-vision');
      const fileset = await vision.FilesetResolver.forVisionTasks(WASM);
      const d = await vision.FaceDetector.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODELO_ROSTRO, delegate: 'GPU' },
        runningMode: 'VIDEO',
        minDetectionConfidence: CONFIANZA_MINIMA,
      });
      return d as unknown as Detector;
    } catch {
      // Ni GPU, ni WASM, ni modelo: se sigue sin detector. Para el documento no cambia nada.
      return null;
    }
  })();
  return promesaDelDetector;
}

/** El detector de FOTOS (modo IMAGE), para el recorte del marco. Aparte y perezoso. */
let promesaDelDetectorDeFotos: Promise<DetectorDeFotos | null> | null = null;

async function cargarDetectorDeFotos(): Promise<DetectorDeFotos | null> {
  promesaDelDetectorDeFotos ??= (async () => {
    try {
      const vision = await import('@mediapipe/tasks-vision');
      const fileset = await vision.FilesetResolver.forVisionTasks(WASM);
      const d = await vision.FaceDetector.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODELO_ROSTRO, delegate: 'GPU' },
        runningMode: 'IMAGE',
        minDetectionConfidence: CONFIANZA_MINIMA,
      });
      return d as unknown as DetectorDeFotos;
    } catch {
      return null;
    }
  })();
  return promesaDelDetectorDeFotos;
}

export interface CapturaAutomatica {
  /** UNA pista, la más urgente. `null` = todo bien, está a punto de disparar. */
  pista: string | null;
  /** 0 a 1. Es el anillo que se llena en el Figma mientras la imagen se sostiene. */
  progreso: number;
  /** Las pastillas del Figma. */
  luzOk: boolean;
  nitidezOk: boolean;
  /** Solo significa algo con exigencias de documento: ¿la cara que toca está delante? */
  rostroImpresoOk: boolean;
  /** false = MediaPipe no cargó. Para 'rostro' esto significa que NO va a disparar. */
  motorCargado: boolean;
}

export interface OpcionesDeCaptura {
  /** Rearma el lazo cuando cambia. La pantalla del documento pasa la cara. */
  clave?: unknown;

  /** No disparar sin la foto impresa en el marco. El anverso del DNI. */
  soloConRostroImpreso?: boolean;

  /**
   * El reverso: sin cara impresa (si aún se ve, no se volteó el documento) Y con la banda
   * del MRZ visible (una mesa no tiene MRZ). Ver la cabecera.
   */
  sinRostroImpreso?: boolean;

  /** El rect EN PANTALLA del recuadro. Sin él, se aproxima con el centro del vídeo. */
  region?: () => DOMRect | null;
}

/**
 * Mira el vídeo y dispara cuando toca.
 *
 * `onFoto` recibe el JPEG en base64 de la mejor foto de la ráfaga — recortada al marco
 * cuando hay exigencias de documento. Se llama UNA vez por disparo.
 */
export function useCapturaAutomatica(
  video: HTMLVideoElement | null,
  objetivo: Objetivo,
  activo: boolean,
  onFoto: (jpegBase64: string) => void,
  opciones: OpcionesDeCaptura = {},
): CapturaAutomatica {
  const [estado, setEstado] = useState<CapturaAutomatica>({
    pista: null,
    progreso: 0,
    luzOk: true,
    nitidezOk: true,
    rostroImpresoOk: true,
    motorCargado: false,
  });

  // La función de la pantalla cambia en cada render; el lazo no debe reiniciarse por eso.
  const alDisparar = useRef(onFoto);
  alDisparar.current = onFoto;
  const laRegion = useRef(opciones.region);
  laRegion.current = opciones.region;

  const { clave, soloConRostroImpreso = false, sinRostroImpreso = false } = opciones;

  useEffect(() => {
    if (video === null || !activo) return;

    let vivo = true;
    let racha = RACHA_INICIAL;
    let detector: Detector | null = null;
    let detectorDeFotos: DetectorDeFotos | null = null;
    let cargaDeFotosTerminada = false;
    let mrzAusenteDesde = 0;
    const lienzo = document.createElement('canvas');
    const recorte = document.createElement('canvas');
    const conExigencia = soloConRostroImpreso || sinRostroImpreso;

    void cargarDetector().then((d) => {
      if (!vivo) return;
      detector = d;
      setEstado((e) => ({ ...e, motorCargado: d !== null }));
    });

    if (conExigencia) {
      void cargarDetectorDeFotos().then((d) => {
        if (!vivo) return;
        detectorDeFotos = d;
        cargaDeFotosTerminada = true;
      });
    }

    /** El marco, en coordenadas de VÍDEO. La aritmética de object-cover. */
    function marcoEnVideo(margen: number): { sx: number; sy: number; sw: number; sh: number } | null {
      const v = video;
      if (v === null || v.videoWidth === 0) return null;
      const vw = v.videoWidth;
      const vh = v.videoHeight;

      const rectV = v.getBoundingClientRect();
      const rectM = laRegion.current?.() ?? null;

      let sx: number, sy: number, sw: number, sh: number;

      if (rectM === null || rectV.width === 0 || rectV.height === 0) {
        sw = vw * ANCHO_DEL_MARCO;
        sh = Math.min(vh, sw / PROPORCION_ID1);
        sx = (vw - sw) / 2;
        sy = (vh - sh) / 2;
      } else {
        const s = Math.max(rectV.width / vw, rectV.height / vh);
        const x0 = (vw - rectV.width / s) / 2;
        const y0 = (vh - rectV.height / s) / 2;
        sx = x0 + (rectM.left - rectV.left) / s;
        sy = y0 + (rectM.top - rectV.top) / s;
        sw = rectM.width / s;
        sh = rectM.height / s;
      }

      const mx = sw * margen;
      const my = sh * margen;
      sx = Math.max(0, sx - mx);
      sy = Math.max(0, sy - my);
      sw = Math.min(sw + 2 * mx, vw - sx);
      sh = Math.min(sh + 2 * my, vh - sy);

      if (sw < 32 || sh < 32) return null;
      return { sx, sy, sw, sh };
    }

    /**
     * ¿Hay una banda de texto denso (el MRZ) en la mitad baja del recorte?
     *
     * Solo píxeles, cero modelos: filas con tinta entre 5% y 50% del ancho y ≥16
     * transiciones claro↔oscuro. Tres líneas de MRZ dejan ~20 filas así; se piden 5.
     */
    function hayBandaMrz(ctx: CanvasRenderingContext2D, ancho: number, alto: number): boolean {
      const desde = Math.floor(alto * 0.45);
      const data = ctx.getImageData(0, desde, ancho, alto - desde).data;
      const filas = alto - desde;

      // Umbral relativo a la media del recorte inferior: robusto a exposición.
      let suma = 0;
      for (let i = 0; i < data.length; i += 4) suma += data[i]! + data[i + 1]! + data[i + 2]!;
      const media = suma / 3 / (ancho * filas);
      const umbral = media * 0.72;

      let filasDeTexto = 0;
      for (let y = 0; y < filas; y++) {
        let tinta = 0;
        let transiciones = 0;
        let previoOscuro = false;
        for (let x = 0; x < ancho; x++) {
          const i = (y * ancho + x) * 4;
          const lum = (data[i]! + data[i + 1]! + data[i + 2]!) / 3;
          const oscuro = lum < umbral;
          if (oscuro) tinta++;
          if (x > 0 && oscuro !== previoOscuro) transiciones++;
          previoOscuro = oscuro;
        }
        const ratio = tinta / ancho;
        if (ratio >= TINTA_MINIMA && ratio <= TINTA_MAXIMA && transiciones >= TRANSICIONES_MINIMAS_POR_FILA) {
          filasDeTexto++;
          if (filasDeTexto >= FILAS_DE_TEXTO_MINIMAS) return true;
        }
      }
      return false;
    }

    /**
     * El análisis del marco: cara impresa y banda MRZ, sobre el mismo recorte.
     *
     *   impresa: 'si' | 'no' | 'cargando' | 'no_disponible'
     *   mrz:     true | false | null (no medible)
     */
    function analizarMarco(): { impresa: 'si' | 'no' | 'cargando' | 'no_disponible'; mrz: boolean | null } {
      const v = video;
      const r0 = v === null ? null : marcoEnVideo(MARGEN_DE_DETECCION);
      if (v === null || r0 === null) return { impresa: 'no_disponible', mrz: null };

      recorte.width = ANCHO_DEL_RECORTE;
      recorte.height = Math.max(32, Math.round((r0.sh / r0.sw) * ANCHO_DEL_RECORTE));
      const ctx = recorte.getContext('2d', { willReadFrequently: true });
      if (ctx === null) return { impresa: 'no_disponible', mrz: null };
      ctx.drawImage(v, r0.sx, r0.sy, r0.sw, r0.sh, 0, 0, recorte.width, recorte.height);

      let impresa: 'si' | 'no' | 'cargando' | 'no_disponible';
      if (!cargaDeFotosTerminada) impresa = 'cargando';
      else if (detectorDeFotos === null) impresa = 'no_disponible';
      else {
        try {
          const r = detectorDeFotos.detect(recorte);
          const visible = r.detections.some((d) => {
            const w = d.boundingBox?.width;
            if (w === undefined) return false;
            const rel = w / ANCHO_DEL_RECORTE;
            return rel >= ROSTRO_IMPRESO_MINIMO && rel <= ROSTRO_IMPRESO_MAXIMO;
          });
          impresa = visible ? 'si' : 'no';
        } catch {
          impresa = 'no_disponible';
        }
      }

      let mrz: boolean | null = null;
      if (sinRostroImpreso) {
        try {
          mrz = hayBandaMrz(ctx, recorte.width, recorte.height);
        } catch {
          mrz = null;
        }
      }

      return { impresa, mrz };
    }

    /** Saca el JPEG, recortado al marco cuando hay exigencias de documento. */
    function foto(): { jpeg: string; detalle: number } | null {
      const v = video;
      if (v === null || v.videoWidth === 0) return null;

      const full = document.createElement('canvas');
      const r0 = conExigencia ? marcoEnVideo(MARGEN_DE_CAPTURA) : null;

      const ctx = full.getContext('2d');
      if (ctx === null) return null;

      if (r0 !== null) {
        full.width = Math.round(r0.sw);
        full.height = Math.round(r0.sh);
        ctx.drawImage(v, r0.sx, r0.sy, r0.sw, r0.sh, 0, 0, full.width, full.height);
      } else {
        full.width = v.videoWidth;
        full.height = v.videoHeight;
        ctx.drawImage(v, 0, 0);
      }

      const g = fotogramaEnGrises(v, lienzo);
      return {
        jpeg: full.toDataURL('image/jpeg', 0.92),
        detalle: g === null ? 0 : medirDetalle(g.grises, g.ancho, g.alto),
      };
    }

    /** La ráfaga: cinco fotos y nos quedamos con la más nítida. Es de v1. */
    async function rafaga(): Promise<void> {
      const fotos: { jpeg: string; detalle: number }[] = [];
      for (let i = 0; i < FOTOS_DE_LA_RAFAGA && vivo; i++) {
        const f = foto();
        if (f !== null) fotos.push(f);
        if (i < FOTOS_DE_LA_RAFAGA - 1) {
          await new Promise((r) => setTimeout(r, MS_ENTRE_FOTOS_DE_LA_RAFAGA));
        }
      }
      if (!vivo) return;

      const i = laMasNitida(fotos.map((f) => f.detalle));
      const mejor = i >= 0 ? fotos[i] : undefined;
      if (mejor !== undefined) alDisparar.current(mejor.jpeg);
    }

    const t = setInterval(() => {
      const g = fotogramaEnGrises(video, lienzo);
      if (g === null) return;

      let hayRostro = false;
      let anchoDelRostro = 0;

      if (detector !== null) {
        try {
          const r = detector.detectForVideo(video, performance.now());
          const d = r.detections[0];
          const caja = d?.boundingBox;
          if (caja !== undefined && video.videoWidth > 0) {
            hayRostro = true;
            anchoDelRostro = caja.width / video.videoWidth;
          }
        } catch {
          // Un fotograma que el detector no digiere no puede tumbar el lazo entero.
        }
      }

      const m = mirar({ ...g, hayRostro, anchoDelRostro }, objetivo);

      // ── Las exigencias del documento, EN VIVO ───────────────────────────────
      const marco = conExigencia ? analizarMarco() : { impresa: 'no_disponible' as const, mrz: null };
      const ahora = performance.now();

      // Anverso: la foto impresa TIENE que verse. Cargando bloquea (ver cabecera);
      // solo el fallo definitivo del motor degrada a capturar por calidad.
      const anversoOk =
        !soloConRostroImpreso || marco.impresa === 'si' || marco.impresa === 'no_disponible';

      // Reverso, parte 1: la cara impresa NO puede verse (si se ve, no se volteó).
      const sinCara = marco.impresa === 'no' || marco.impresa === 'no_disponible';

      // Reverso, parte 2: la banda del MRZ tiene que verse — con válvula de paciencia.
      // El reloj corre solo cuando ÚNICAMENTE falta el MRZ: calidad bien, documento
      // volteado, y la banda sin aparecer (reflejo sobre el laminado, MRZ gastado).
      if (sinRostroImpreso && m.bueno && sinCara && marco.mrz === false) {
        if (mrzAusenteDesde === 0) mrzAusenteDesde = ahora;
      } else {
        mrzAusenteDesde = 0;
      }
      const pacienciaAgotada =
        mrzAusenteDesde !== 0 && ahora - mrzAusenteDesde > MS_DE_PACIENCIA_CON_EL_MRZ;
      const mrzOk = marco.mrz !== false || pacienciaAgotada;

      const reversoOk = !sinRostroImpreso || (sinCara && mrzOk);
      const exigenciaCumplida = anversoOk && reversoOk;

      racha = acumular(racha, m.bueno && exigenciaCumplida);

      setEstado((e) => ({
        ...e,
        // La pista del core manda; después, la exigencia que falte. Siempre QUÉ HACER.
        pista:
          pistaDeEncuadre(m) ??
          (conExigencia && marco.impresa === 'cargando'
            ? 'Preparando la detección…'
            : !anversoOk
              ? 'Coloca la foto de tu DNI dentro del recuadro'
              : sinRostroImpreso && !sinCara
                ? 'Voltea tu DNI: ahora el reverso'
                : sinRostroImpreso && !mrzOk
                  ? 'Acerca el reverso: las líneas de la parte baja deben leerse'
                  : null),
        progreso: Math.min(1, racha.seguidas / MIRADAS_BUENAS_PARA_DISPARAR),
        luzOk: m.calidad.luz === 'ok',
        nitidezOk: m.calidad.nitidez === 'ok',
        rostroImpresoOk: exigenciaCumplida,
      }));

      // Cargando también bloquea el disparo del anverso (no solo la pista).
      const listoParaDisparar =
        exigenciaCumplida && !(soloConRostroImpreso && marco.impresa === 'cargando');

      if (racha.dispara && listoParaDisparar) {
        clearInterval(t); // se para: un disparo por clave, no una ráfaga infinita
        void rafaga();
      } else if (racha.dispara) {
        // La racha llegó pero la exigencia aún carga: se recorta para no disparar en cuanto
        // el detector despierte con la escena equivocada delante.
        racha = RACHA_INICIAL;
      }
    }, MS_ENTRE_MIRADAS);

    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [video, objetivo, activo, clave, soloConRostroImpreso, sinRostroImpreso]);

  return estado;
}
